/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: BI Dashboard
 * (C) Copyright IBM Corp. 2021
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import { FlexLayout, FlexItem, Label } from '@ba/ba-ui-carbon-toolkit';
import { Dropdown, DropdownSkeleton } from '@ba/ba-ui-carbon';
import StringResources from 'nls/StringResources';
import { getColorStyle } from '../../utilities/ColorUtil';
import PaletteHelper from '../../utilities/PaletteHelper';
import $ from 'jquery';

const MAX_PALETTE_COLOR_SIZE = 12; // AS per UX Design
const MANAGE_COLOR_PALETTE_LINK_ID = 'manageColorPaletteId';
const MRU_PALETTES_LABEL_ID = 'mruPalettesLabelId';
const All_PALETTES_LABEL_ID = 'allPalettesLabelId';

const observerOptions = {
	attributes: true
};

class DropDownPalette extends Component {
	static propTypes = {
		className: PropTypes.string,
		/** Current selected palette id */
		selectedId: PropTypes.string.isRequired,
		/** Default selected palette id if cannot find the palette selected id */
		defaultSelectedId: PropTypes.string.isRequired,
		/** paletteService */
		paletteService: PropTypes.object.isRequired, // was glass context
		/** callback function to get palettes */
		getPalettes: PropTypes.func.isRequired,
		/** callback function to call during selection change */
		onChange: PropTypes.func.isRequired,
		/** callback function to call to manage color palette */
		onManageColorPalette: PropTypes.func.isRequired,
		/** Palette type name supported. One of  */
		paletteTypeName: PropTypes.oneOf(['ColorPalette', 'HeatPalette', 'ConditionalPalette'])
	};

	static defaultProps = {
		className: ''
	};

	constructor (props) {
		super(props);
		const { selectedId } = this.props;
		this.state= {
			selectedPalette: null,
			palettes: null,
			selectedId: selectedId
		};
		const { getPalettes, paletteService, paletteTypeName } = this.props;
		this.paletteHelper = new PaletteHelper({ paletteService,  getPalettesFn: getPalettes, paletteName: paletteTypeName });
		this._optionLabelObservers = {};
	}

	componentDidUpdate () {
		const { selectedId: propSelectedId } = this.props;
		const { selectedId: stateSelectedId } = this.state;
		const promise = (propSelectedId != stateSelectedId) ? this._loadPalettes() : Promise.resolve();
		return promise
			.then(() => {
				if (!this._observer && this.instanceDomNode) {
					this._dropDown$ = $(this.instanceDomNode).find('.bx--dropdown.authoring-palette-dropdown .bx--list-box__menu');
					if (this._dropDown$.length) {
						this._observer = new window.MutationObserver(this._processListBoxMenuMutationObserver.bind(this));
						this._observer.observe(this._dropDown$[0], observerOptions);
					}
				}
			});
	}

	componentDidMount () {
		return this._loadPalettes();
	}

	componentWillUnmount () {
		this._disconnectAllMutationObservers();
		this.instanceDomNode = null;
	}

	render() {
		const { className } = this.props;
		const { selectedPalette, palettes } = this.state;
		const clsNames = 'authoring-palette-dropdown' + (className ? ` ${className}` : '');
		return selectedPalette ? (
			<div
				ref={(instanceDomNode) => {
					// Carbon Dropdown re-render the Component each time a new palette selection is selected
					// In this case it toggles between removing the instance in the DOM and then add it back
					// During the removal of the instance node from the DOM clean up all mutation observers associated with that DOM instance node
					this._disconnectAllMutationObservers();
					this.instanceDomNode = instanceDomNode;
				}}
			>
				<Dropdown
					id={'authoring-palette-dropdownId'}
					className={clsNames}
					itemToElement={this._itemToElement.bind(this)}
					itemToString={this._itemToString.bind(this)}
					items={palettes}
					label={''}
					selectedItem={selectedPalette}
					onChange={this.onChange.bind(this)}
				/>
			</div>
		) : (
			<DropdownSkeleton/>
		);
	}

	onChange (palette = {}) {
		const selectedItem = palette.selectedItem;
		const { onManageColorPalette, onChange } = this.props;
		if (selectedItem.content.optionLink) {
			onManageColorPalette();
		} else {
			this.setState({ selectedPalette: selectedItem });
			onChange(selectedItem);
		}
	}

	_loadPalettes () {
		if (!this._loadPaletteInProgress) {
			this._loadPaletteInProgress = this.paletteHelper.getPaletteGroupBySections();
		}
		return this._loadPaletteInProgress
			.then(palettesGroupBySection => {
				const { defaultSelectedId, selectedId } = this.props;
				let newSelectedId;
				let selectedPalette = this.paletteHelper.getPalette(selectedId);
				if (selectedPalette) {
					newSelectedId = selectedId;
				} else {
					selectedPalette = this.paletteHelper.getPalette(defaultSelectedId);
					newSelectedId = defaultSelectedId;
				}
				if (!selectedPalette) {
					return Promise.reject(`Cannot find the selected palettes: ${selectedId} and ${defaultSelectedId}`);
				}
				const palettes = [...this._getPaletteContent(palettesGroupBySection)];
				this.setState({
					selectedId: newSelectedId,
					selectedPalette: selectedPalette,
					palettes: palettes
				});
			})
			.finally(() => {
				delete this._loadPaletteInProgress;
			});
	}

	_itemToElement (palette) {
		if (!palette || !palette.content) {
			return null;
		}
		const optionLink = palette.content.optionLink;
		const sectionLabel = palette.content.sectionLabel;
		if (optionLink) {
			return (
				<Label
					className={'authoring-palette-dropdown-manage-color-palette'}
					label={optionLink}
					tabIndex={0}
					title={optionLink}
				/>
			);
		} else if (sectionLabel) {
			return (
				<Label
					className={'authoring-palette-dropdown-option-label'}
					label={sectionLabel}
					tabIndex={-1}
					title={sectionLabel}
				/>
			);
		}
		return this._renderDropDownPaletteFills(palette);
	}

	_itemToString (palette) {
		if (!palette || !palette.content) {
			return null;
		}
		return this._renderDropDownPaletteFills(palette);
	}

	_renderDropDownPaletteFills (palette = {}) {
		const fills = palette.content.fills || [];
		return (
			<FlexLayout
				className={'authoring-palette-dropdown-palette'}
				aria-label={palette.content.label}
				title={palette.content.label}
			>
				{fills.map((color, index) => (
					<FlexItem
						className={'authoring-palette-dropdown-palette-color'}
						key={index}
						data-index={index}
						data-value={color}
						style= { color && getColorStyle(color.color, color.fill || color, color.pattern) }
					/>
				))}
			</FlexLayout>
		);
	}

	_getPaletteContent (palettesGroup = {}) {
		const palettes = [];
		const sections = this.paletteHelper.getPaletteSectionOrder();
		let hasAllPalettesLabel = palettesGroup.local.length  || palettesGroup.custom.length || palettesGroup.global.length || palettesGroup.system.length || palettesGroup.legacy.length;
		sections.forEach(section => {
			const palette = palettesGroup[section] || [];
			if (section === 'mru') {
				if (palette.length) {
					palettes.push({
						content: {
							id: MRU_PALETTES_LABEL_ID,
							sectionLabel: StringResources.get('mruPalettes')
						}
					});
				}
			} else if (hasAllPalettesLabel) {
				palettes.push({
					content: {
						id: All_PALETTES_LABEL_ID,
						sectionLabel: StringResources.get('allPalettes')
					}
				});
				hasAllPalettesLabel = false;
			}
			palette.forEach(item => {
				if (item.content.fills) {
					item.content.fills.length = (item.content.fills.length <= MAX_PALETTE_COLOR_SIZE) ? item.content.fills.length : MAX_PALETTE_COLOR_SIZE;
				}
			});
			palettes.push(...palette);
		});

		// Now add the 'Manage color palettes' link
		palettes.push({
			id: MANAGE_COLOR_PALETTE_LINK_ID,
			content: {
				id: MANAGE_COLOR_PALETTE_LINK_ID,
				optionLink: StringResources.get('manageColorPalettes')
			}
		});
		return palettes;
	}

	_processListBoxMenuMutationObserver (mutations) {
		const mutation = mutations.find(item => {
			return item.attributeName === 'aria-activedescendant';
		});
		if (mutation && mutation.target.getAttribute('aria-activedescendant')) {
			const optionLabelNodes = $(this._dropDown$[0]).find('.authoring-palette-dropdown-option-label');
			if (optionLabelNodes.length) {
				optionLabelNodes.each((index, optionNode) => {
					const node$ = $(optionNode).closest('.bx--list-box__menu-item');
					if (node$.length) {
						if (!this._optionLabelObservers[index]) {
							this._optionLabelObservers[index] = new window.MutationObserver(this._processMutationOptionLabelObserver.bind(this));
						}
						this._optionLabelObservers[index].observe(node$[0], observerOptions);
						node$.addClass('authoring-palette-menu-item-label');
					}
				});
			}
		} else {
			this._disconnectMutationOptionLabelObserver();
		}
	}

	_processMutationOptionLabelObserver (mutations) {
		mutations.forEach(mutation => {
			if (mutation.attributeName === 'class') {
				const $node = $(mutation.target);
				if (!$node.hasClass('authoring-palette-menu-item-label')) {
					$node.addClass('authoring-palette-menu-item-label');
				}
			}
		});
	}

	_disconnectMutationOptionLabelObserver () {
		const keys = Object.keys(this._optionLabelObservers);
		keys.forEach(key => {
			this._optionLabelObservers[key].disconnect();
		});
		this._optionLabelObservers = {};
	}

	_disconnectAllMutationObservers () {
		this._disconnectMutationOptionLabelObserver();
		if (this._observer) {
			this._observer.disconnect();
			this._observer = null;
		}
		if (this._dropDown$) {
			this._dropDown$ = null;
		}
	}
}

export default observer(DropDownPalette);



// WEBPACK FOOTER //
// ./src/js/components/quickPaletteSelection/DropDownPalette.js